import Config from 'config';
import React from 'react';
import ReactInputMask from 'react-input-mask';
import { Box } from '@apia/theme';
import useContext from '../../../../hooks/useContext';
import MakeField from '../fieldsStore/MakeField';
import FieldContext from '../fieldsStore/FieldContext';
import { IApiaInput } from '../../../../types';
import IProperty from '../../../../JSApi/constants/IProperty';
import { getFieldErrorStyles } from '../style';
import { formatMessage, parseMask } from '../../../../utils/util';
import NumberApiaInput from './NumberApiaInput';
import ReadonlyApiaInput from './readonly/ApiaInput';
import useApiaFieldCommonProps from './useApiaFieldCommonProps';
import { ApiaComponentContextPrimitive } from '../apiaComponent/ApiaComponentContext';
import propsStore from '../../../../store/propsStore/propsStore';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import { useApiaFormContext } from '../page/ApiaForm';

const DEFAULT_MASK_PLACEHOLDER = '_';
const DEFAULT_INPUT_LENGTH = 255;

export const ApiaInputContext = React.createContext<
  | ({
      onChange: (
        newValue: string | React.ChangeEvent<HTMLInputElement>,
      ) => unknown;
    } & Record<string, unknown>)
  | undefined
>(undefined);

const ApiaInputRenderer = React.forwardRef<HTMLInputElement>((_, ref) => {
  const {
    state: { isValid, value },
    setValue,
    fireEvent,
  } = useContext(FieldContext);
  const { field, fieldId } = useContext(ApiaComponentContextPrimitive);
  const {
    attLabel,
    attName,
    valueType,
    fieldType,
    length,
    mask,
    maskPlaceholder,
    // TODO: Revisar estas propiedades
    /*
    decimalZeroes,
    cantDecimals, */
  } = field as IApiaInput;

  const { name, ...props } = usePropsSelector(fieldId, (current) => current);

  const actualFieldType =
    fieldType === 'password'
      ? 'password'
      : (props[IProperty.PROPERTY_INPUT_TYPE] ?? 'text');

  const { identifier, isReadonly } = useApiaFieldCommonProps({
    className: 'input__field',
  });

  const contextValue = React.useMemo(
    () => ({
      name: name || attName,
      maxLength: mask ? undefined : length || DEFAULT_INPUT_LENGTH,
      title:
        props[IProperty.PROPERTY_TITLE] ||
        props[IProperty.PROPERTY_TOOLTIP] ||
        attLabel,
      type: actualFieldType,
      required: props[IProperty.PROPERTY_REQUIRED],
      disabled: props[IProperty.PROPERTY_DISABLED],
      placeholder: props[IProperty.PROPERTY_PLACE_HOLDER],
      autofocus: props[IProperty.PROPERTY_AUTO_FOCUS] || undefined,
      autocomplete: props[IProperty.PROPERTY_AUTO_COMPLETE],
      readOnly: isReadonly,
      'aria-label': props[IProperty.PROPERTY_ARIA_LABEL],
      'aria-description': props[IProperty.PROPERTY_ARIA_DESCRIPTION],
      'data-variant': 'forms.input',
      sx: {
        variant: 'forms.input',
        ...getFieldErrorStyles(isValid),
        color: props[IProperty.PROPERTY_VALUE_COLOR]
          ? `${props[IProperty.PROPERTY_VALUE_COLOR]} !important`
          : '',
        width: props[IProperty.PROPERTY_SIZE]
          ? `${props[IProperty.PROPERTY_SIZE] as string}px`
          : '100%',
      },
      value: value as string,
      onChange: (ev: string | React.ChangeEvent<HTMLInputElement>) => {
        const autoFillFields = document.querySelectorAll<HTMLInputElement>(
          'input:-webkit-autofill, input:autofill',
        );

        autoFillFields?.forEach((input) => {
          input.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
          input.dispatchEvent(new FocusEvent('blur'));
          input.blur();
        });

        if (autoFillFields.length > 0) autoFillFields.forEach((e) => e.focus());

        setValue(typeof ev === 'string' ? ev : ev.target.value);
      },
      onBlur: () => fireEvent('onChange'),
      ref,
      ...identifier,
    }),
    [
      actualFieldType,
      attLabel,
      attName,
      fireEvent,
      identifier,
      isReadonly,
      isValid,
      length,
      mask,
      name,
      props,
      ref,
      setValue,
      value,
    ],
  );
  const { isFormReadOnly } = useApiaFormContext();
  const wrapperSx = React.useMemo(
    () => ({
      input: contextValue.sx,
    }),
    [contextValue.sx],
  );

  if (isFormReadOnly || props[IProperty.PROPERTY_INPUT_AS_TEXT]) {
    return (
      <ApiaInputContext.Provider value={contextValue}>
        <ReadonlyApiaInput />
      </ApiaInputContext.Provider>
    );
  }

  if (valueType === 'N' && actualFieldType === 'text')
    return (
      <ApiaInputContext.Provider value={contextValue}>
        <NumberApiaInput />
      </ApiaInputContext.Provider>
    );

  return (
    <Box className="inputField__wrapper" sx={wrapperSx}>
      <ReactInputMask
        {...(contextValue as unknown as Record<string, unknown>)}
        mask={parseMask(mask)}
        type={actualFieldType}
        maskPlaceholder={maskPlaceholder || DEFAULT_MASK_PLACEHOLDER}
      />
    </Box>
  );
});

const ApiaInput = MakeField(ApiaInputRenderer, {
  preSynchronize({
    fieldContext: {
      isReadonly,
      state: { value, lastValidValue, isValid },
      componentContext: { field, fieldId },
      setError,
    },
  }) {
    const { regExpMessage } = propsStore.getFieldProps(fieldId);
    if (isReadonly) return 'cancel';
    const { mask, regExp, maskPlaceholder } = field as IApiaInput;
    const placeHolder = maskPlaceholder || DEFAULT_MASK_PLACEHOLDER;

    if (value && regExp && !(value as string).match(regExp)) {
      setError(
        formatMessage(regExpMessage || Config.MSG_INVALID_REG_EXP, {
          TOK1: '',
        }),
      );
      return 'cancel';
    }

    const parsedValue = (value as string).replaceAll(placeHolder, '9');
    const parsedMask = parseMask(mask);
    if (value && parsedValue === parsedMask) return 'cancel';

    return value === lastValidValue
      ? 'cancel'
      : isValid
        ? true
        : 'emittedWithError';
  },
});

export default ApiaInput;
